/**
 * Recurrence Utilities
 *
 * Pure functions for working with Google-Calendar-style recurrence rules
 * on the frontend. Kept in `utils/` (next to formatDate, formatLabel,
 * jobProgress, etc.) so any component — the picker, the badge, the
 * Repeats list/detail pages — can reach for the same primitives.
 *
 * The shapes here intentionally mirror the backend `recurrence_series`
 * columns so the rule object can be POSTed verbatim:
 *
 *   - frequency_unit: 'day' | 'week' | 'month' | 'year'
 *   - interval: positive int (every N units)
 *   - by_weekday: int[0..6] where 0=Sunday
 *   - start_date: 'YYYY-MM-DD'
 *   - end_type: 'never' | 'on' | 'after'
 *   - end_date: 'YYYY-MM-DD' | null
 *   - end_count: positive int | null
 *
 * 'never' is the default and is stored as-is: a series runs until someone
 * stops it. The server creates each job when its date arrives rather than
 * a year of them up front (beta feedback, Oct 2026), so nothing needs a
 * horizon any more. expandOccurrences() below still caps itself at a year,
 * but only to bound a preview list.
 */

/* ============================================
 * TYPES
 * ============================================ */

/** Frequency unit values accepted by the backend service. */
export type FrequencyUnit = 'day' | 'week' | 'month' | 'year';

/** Terminator kinds. 'never' (the default) is open-ended. */
export type EndType = 'never' | 'on' | 'after';

/**
 * The serializable rule object. Matches the columns on
 * `recurrence_series` so it can be posted directly.
 */
export interface RecurrenceRule {
	frequency_unit: FrequencyUnit;
	interval: number;
	/** Sun=0 .. Sat=6. Only meaningful when frequency_unit='week'. */
	by_weekday: number[];
	/** YYYY-MM-DD anchor date. */
	start_date: string;
	end_type: EndType;
	/** YYYY-MM-DD. Required (or auto-derived) when end_type='on'. */
	end_date: string | null;
	/** Required when end_type='after'. */
	end_count: number | null;
}

/**
 * UI-only mode signal. Distinguishes "no recurrence" from a configured
 * rule without forcing the rule object to carry a sentinel value.
 */
export type RecurrenceMode = 'none' | 'preset' | 'custom';

/** Preset entry surfaced in the picker dropdown. */
export interface RecurrencePreset {
	id: string;
	label: string;
	build: (start: Date) => RecurrenceRule;
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Hard cap matching the user spec: a series cannot extend past 1 year. */
export const MAX_HORIZON_DAYS = 366;

/** Max number of occurrences the picker preview will compute. */
export const MAX_OCCURRENCES = 366;

/** Sun=0 .. Sat=6, mirrors JavaScript Date.getDay() and the backend. */
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAY_LONG  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** Single-letter labels used inside the weekday chip circles (S M T W T F S). */
export const WEEKDAY_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Month names used by the "Annually on …" preset label. */
export const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Ordinal labels for "Monthly on the Nth …" presets. */
const ORDINAL_LABEL = ['first', 'second', 'third', 'fourth', 'fifth'];

/* ============================================
 * RULE FACTORIES
 * ============================================ */

/** A do-nothing baseline rule — useful as the picker's initial state. */
export function emptyRule(startDate?: string): RecurrenceRule {
	return {
		frequency_unit: 'week',
		interval: 1,
		by_weekday: [],
		start_date: startDate || toIsoDate(new Date()),
		/* Open-ended by default — "Reoccurring work orders should not have
		   an end date" (beta feedback, Oct 2026). */
		end_type: 'never',
		end_date: null,
		end_count: null,
	};
}

/**
 * Default end_date — pre-filled when the user switches the terminator to
 * 'on' without picking a date. One year out is just a starting suggestion;
 * any later date is allowed.
 */
export function defaultEndDate(startIso: string): string {
	const d = new Date(`${startIso}T00:00:00`);
	d.setFullYear(d.getFullYear() + 1);
	return toIsoDate(d);
}

/* ============================================
 * PRESETS — Google-Calendar parity
 * ============================================ */

/**
 * Build the seven preset options Google Calendar offers, anchored to a
 * specific start date. The labels embed the date's weekday / month /
 * day so the dropdown reads naturally ("Weekly on Monday", "Monthly on
 * the fourth Monday", "Annually on May 25").
 *
 * The `Does not repeat` and `Custom...` rows are NOT in this list —
 * they're separate UI affordances handled by the picker.
 */
export function presetOptions(start: Date): RecurrencePreset[] {
	const dow = start.getDay();
	const dowName = WEEKDAY_LONG[dow];
	const month = MONTH_LONG[start.getMonth()];
	const day = start.getDate();
	const ordinal = ORDINAL_LABEL[Math.floor((day - 1) / 7)] ?? 'fifth';

	// "Last weekday-of-month" preset only makes sense when the start
	// date IS the last weekday of its month — otherwise the label lies.
	const lastDow = isLastWeekdayOfMonth(start);

	const startIso = toIsoDate(start);

	const presets: RecurrencePreset[] = [
		{
			id: 'daily',
			label: 'Daily',
			build: () => ({ ...emptyRule(startIso), frequency_unit: 'day', interval: 1, by_weekday: [] }),
		},
		{
			id: 'weekly_on_dow',
			label: `Weekly on ${dowName}`,
			build: () => ({ ...emptyRule(startIso), frequency_unit: 'week', interval: 1, by_weekday: [dow] }),
		},
		{
			id: 'monthly_on_ordinal_dow',
			label: `Monthly on the ${ordinal} ${dowName}`,
			/* Note: we encode this as a plain "monthly, every interval=1"
			 * rule anchored to the start date. The backend's monthly
			 * walker preserves day-of-month rather than ordinal-weekday,
			 * which differs from Google's exact semantics but is the
			 * pragmatic match for "same date every month". A future
			 * iteration can add BYDAY=NthSUN support to handle the
			 * Google-strict ordinal-weekday case. */
			build: () => ({ ...emptyRule(startIso), frequency_unit: 'month', interval: 1, by_weekday: [] }),
		},
	];

	if (lastDow) {
		presets.push({
			id: 'monthly_on_last_dow',
			label: `Monthly on the last ${dowName}`,
			// Same caveat as the ordinal preset — encoded as plain monthly.
			build: () => ({ ...emptyRule(startIso), frequency_unit: 'month', interval: 1, by_weekday: [] }),
		});
	}

	presets.push({
		id: 'annually',
		label: `Annually on ${month} ${day}`,
		build: () => ({ ...emptyRule(startIso), frequency_unit: 'year', interval: 1, by_weekday: [] }),
	});

	presets.push({
		id: 'every_weekday',
		label: 'Every weekday (Monday to Friday)',
		build: () => ({ ...emptyRule(startIso), frequency_unit: 'week', interval: 1, by_weekday: [1, 2, 3, 4, 5] }),
	});

	return presets;
}

/**
 * Try to match an arbitrary rule against the preset list — used by the
 * picker on edit-mode so the dropdown can render "Weekly on Monday"
 * instead of forcing "Custom..." for a rule that started life as a
 * preset. Returns the preset id when the rule matches one byte-for-byte
 * (modulo end terminators), or null when the rule is genuinely custom.
 */
export function matchPresetId(rule: RecurrenceRule, start: Date): string | null {
	for (const p of presetOptions(start)) {
		const built = p.build(start);
		if (
			built.frequency_unit === rule.frequency_unit &&
			built.interval === rule.interval &&
			JSON.stringify(built.by_weekday) === JSON.stringify(rule.by_weekday)
		) {
			return p.id;
		}
	}
	return null;
}

/* ============================================
 * FORMATTERS
 * ============================================ */

/**
 * Human-readable one-liner used by the badge and the Repeats list page.
 * Reads naturally for both presets and custom rules:
 *   - "Daily"
 *   - "Every 3 days"
 *   - "Weekly on Mon, Wed"
 *   - "Every 2 weeks on Fri"
 *   - "Monthly"
 *   - "Yearly"
 */
export function formatRecurrenceSummary(rule: RecurrenceRule): string {
	const i = rule.interval;
	const unit = rule.frequency_unit;

	if (unit === 'day') {
		return i === 1 ? 'Daily' : `Every ${i} days`;
	}
	if (unit === 'week') {
		const days = (rule.by_weekday || []).map((d) => WEEKDAY_SHORT[d]).join(', ');
		const prefix = i === 1 ? 'Weekly' : `Every ${i} weeks`;
		return days ? `${prefix} on ${days}` : prefix;
	}
	if (unit === 'month') {
		return i === 1 ? 'Monthly' : `Every ${i} months`;
	}
	return i === 1 ? 'Yearly' : `Every ${i} years`;
}

/**
 * Format the terminator portion of a rule into a trailing fragment the
 * Repeats list can append to the summary:
 *   - "until Jun 1, 2027"
 *   - "for 12 occurrences"
 *   - "" (when 'never')
 */
export function formatRecurrenceEnd(rule: RecurrenceRule): string {
	if (rule.end_type === 'after' && rule.end_count) {
		return `for ${rule.end_count} occurrence${rule.end_count === 1 ? '' : 's'}`;
	}
	if (rule.end_type === 'on' && rule.end_date) {
		const d = new Date(`${rule.end_date}T00:00:00`);
		return `until ${d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
	}
	return '';
}

/* ============================================
 * OCCURRENCE EXPANSION (client preview)
 * ============================================ */

/**
 * Frontend mirror of the backend RecurrenceGenerator. Used by the
 * picker to show the user a live "X occurrences" count and by the
 * Repeats detail page when the server hasn't yet returned the full
 * occurrence set.
 *
 * Returns ISO YYYY-MM-DD strings, ascending and deduplicated. Caps
 * itself at MAX_OCCURRENCES + MAX_HORIZON_DAYS to match the server.
 */
export function expandOccurrences(rule: RecurrenceRule): string[] {
	if (!rule.start_date) return [];
	const start = parseIsoDate(rule.start_date);
	const horizon = addDays(start, MAX_HORIZON_DAYS);

	// Effective end horizon = min(user-set end, hard cap).
	let endDate = rule.end_date ? parseIsoDate(rule.end_date) : horizon;
	if (endDate > horizon) endDate = horizon;
	const endCount = rule.end_type === 'after' && rule.end_count
		? Math.min(rule.end_count, MAX_OCCURRENCES)
		: MAX_OCCURRENCES;

	const out: string[] = [];

	if (rule.frequency_unit === 'day') {
		let cursor = new Date(start);
		while (out.length < MAX_OCCURRENCES) {
			if (cursor > endDate) break;
			out.push(toIsoDate(cursor));
			if (rule.end_type === 'after' && out.length >= endCount) break;
			cursor = addDays(cursor, rule.interval);
		}
		return out;
	}

	if (rule.frequency_unit === 'week') {
		const dows = (rule.by_weekday && rule.by_weekday.length > 0)
			/* Array.from + new Set rather than `[...new Set(...)]` so the
			   module compiles under the project's pre-ES2015 target. */
			? Array.from(new Set(rule.by_weekday)).sort((a, b) => a - b)
			: [start.getDay()];
		let weekAnchor = startOfWeekSunday(start);
		while (out.length < MAX_OCCURRENCES) {
			let weekHasFutureDay = false;
			for (const dow of dows) {
				const candidate = addDays(weekAnchor, dow);
				if (candidate < start) continue;
				if (candidate > endDate) return out;
				out.push(toIsoDate(candidate));
				weekHasFutureDay = true;
				if (rule.end_type === 'after' && out.length >= endCount) return out;
				if (out.length >= MAX_OCCURRENCES) return out;
			}
			weekAnchor = addDays(weekAnchor, 7 * rule.interval);
			if (!weekHasFutureDay && weekAnchor > endDate) break;
		}
		return out;
	}

	if (rule.frequency_unit === 'month') {
		let cursor = new Date(start);
		while (out.length < MAX_OCCURRENCES) {
			if (cursor > endDate) break;
			out.push(toIsoDate(cursor));
			if (rule.end_type === 'after' && out.length >= endCount) break;
			cursor = addMonthsNoOverflow(cursor, rule.interval);
		}
		return out;
	}

	// frequency_unit === 'year'
	let cursor = new Date(start);
	while (out.length < MAX_OCCURRENCES) {
		if (cursor > endDate) break;
		out.push(toIsoDate(cursor));
		if (rule.end_type === 'after' && out.length >= endCount) break;
		cursor = new Date(cursor);
		cursor.setFullYear(cursor.getFullYear() + rule.interval);
	}
	return out;
}

/* ============================================
 * DATE HELPERS — kept private to the module so the public API is just
 * the rule type + the rule-shaped functions above.
 * ============================================ */

/** YYYY-MM-DD from a Date, computed in the local timezone. */
function toIsoDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a midnight-local Date. */
function parseIsoDate(s: string): Date {
	return new Date(`${s}T00:00:00`);
}

function addDays(d: Date, n: number): Date {
	const next = new Date(d);
	next.setDate(next.getDate() + n);
	return next;
}

function startOfWeekSunday(d: Date): Date {
	return addDays(d, -d.getDay());
}

/**
 * Adds N months without rolling over the end-of-month — e.g. Jan 31 +
 * 1 month → Feb 28 (or 29 in a leap year), matching Carbon's
 * addMonthsNoOverflow on the server.
 */
function addMonthsNoOverflow(d: Date, n: number): Date {
	const target = new Date(d);
	const originalDay = target.getDate();
	target.setDate(1);
	target.setMonth(target.getMonth() + n);
	const lastOfTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
	target.setDate(Math.min(originalDay, lastOfTarget));
	return target;
}

function isLastWeekdayOfMonth(d: Date): boolean {
	const next = addDays(d, 7);
	return next.getMonth() !== d.getMonth();
}
